// src/pages/banner/BannerDetail.jsx
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import Loading from "../../components/Loading";
import { bannerService } from "../../../../services/api/bannerService";
import { motion } from "framer-motion";
import BackButton from "../../components/BackButton";

/* ----------------------- Variants ----------------------- */
const fadeUp = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.18 } },
};

/* ----------------------- Helpers ----------------------- */

const ContentSkeleton = () => (
  <div className="space-y-3">
    <div className="h-4 w-5/6 animate-pulse rounded bg-gray-200" />
    <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
    <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
    <div className="h-4 w-4/5 animate-pulse rounded bg-gray-200" />
  </div>
);

const ThumbSkeleton = () => (
  <div className="aspect-video w-full overflow-hidden rounded-xl border bg-gray-100">
    <div className="h-full w-full animate-pulse bg-gray-200" />
  </div>
);

/* ----------------------- Component ----------------------- */
const BannerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token_client } = useContext(AuthContext);

  // Loading tách bạch
  const [metaLoading, setMetaLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  const [data, setData] = useState({
    name: "",
    txtUrl: "",
    thumbnail: "",
    createdBy: "",
    createdAt: "",
  });
  const [fileContent, setFileContent] = useState(""); // HTML/text từ txtUrl

  const canPreview = useMemo(() => !!data.thumbnail, [data.thumbnail]);

  const fetchDetail = useCallback(async () => {
    try {
      setMetaLoading(true);
      const res = await bannerService.get_detail(token_client, id);
      const payload = res?.data?.data || res?.data || {};

      const next = {
        name: payload.name || "",
        txtUrl: payload.txtUrl || "",
        thumbnail: payload.thumbnail || "",
        createdBy: payload.createdBy || "",
        createdAt: payload.createdAt || payload.created_at || "",
      };
      setData(next);

      if (next.txtUrl) {
        setContentLoading(true);
        try {
          // Kiểm tra xem txtUrl là URL hay HTML content
          if (
            next.txtUrl.startsWith("http://") ||
            next.txtUrl.startsWith("https://")
          ) {
            // Nếu là URL thì fetch
            const resp = await fetch(next.txtUrl);
            const text = await resp.text();
            setFileContent(text || "");
          } else {
            // Nếu là HTML content trực tiếp thì dùng luôn
            setFileContent(next.txtUrl);
          }
        } catch (e) {
          toast.dismiss();
          console.error("Lỗi khi tải nội dung:", e);
        } finally {
          setContentLoading(false);
        }
      } else {
        setFileContent("");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.message ||
          "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Error:", error);
    } finally {
      setMetaLoading(false);
    }
  }, [id, token_client]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Loading tổng (chỉ dùng cho lần đầu)
  if (metaLoading && !data.name) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto rounded-lg border bg-white">
      {/* Sticky header (desktop) */}
      <motion.div
        className="sticky top-0 z-10 border-b bg-white/80 px-3 py-3 backdrop-blur md:px-6 md:py-4"
        variants={fadeUp}
        initial="hidden"
        animate="show"
      >
        <div className="flex items-center justify-between gap-2">
          <BackButton
            ariaLabel="Quay lại"
            className="absolute left-3 top-6"
            onClick={() => navigate(-1)}
          />
          <h1
            className="line-clamp-2 flex-1 text-center font-sf-semibold text-lg leading-tight text-[#0457C2] md:text-2xl"
            title={data.name}
          >
            {data.name || "—"}
          </h1>
          <div className="w-[70px]" aria-hidden />
        </div>
        {/* meta nhỏ */}
        <div className="mt-2 flex items-center justify-center gap-3 text-xs text-gray-500 md:text-sm">
          {data.createdBy && (
            <span>
              Tác giả: <b>{data.createdBy}</b>
            </span>
          )}
          {data.createdAt && (
            <>
              <span>•</span>
              <span>Ngày tạo: {data.createdAt}</span>
            </>
          )}
        </div>
      </motion.div>

      {/* Meta section */}
      <motion.div
        className="grid grid-cols-1 gap-4 px-3 py-4 md:px-6 md:py-6"
        variants={fadeUp}
        initial="hidden"
        animate="show"
      >
        <div className="md:col-span-1">
          {metaLoading ? (
            <ThumbSkeleton />
          ) : (
            <button
              type="button"
              className="group relative w-full overflow-hidden rounded-xl border bg-gray-50"
              disabled={!canPreview}
              aria-label="Xem ảnh lớn"
            >
              {data.thumbnail ? (
                <img
                  src={data.thumbnail}
                  alt={data.name || "Banner"}
                  className="aspect-video w-full object-cover transition"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = "/images/banner-fallback.jpg";
                  }}
                  style={{ filter: "blur(0px)" }}
                />
              ) : (
                <div className="grid aspect-video w-full place-items-center text-sm text-gray-400">
                  Không có hình ảnh
                </div>
              )}
            </button>
          )}
        </div>

        {/* (Tuỳ chọn) Cột thông tin khác */}
        {/* <div className="md:col-span-2">...</div> */}
      </motion.div>

      {/* Content */}
      <motion.div
        className="pb-[80px] md:pb-8 px-3 md:px-6"
        variants={fadeUp}
        initial="hidden"
        animate="show"
      >
        <div className="rounded-xl bg-white px-3 py-3 md:px-4 md:py-4">
          {contentLoading ? (
            <ContentSkeleton />
          ) : fileContent ? (
            <motion.div
              className="
                  prose max-w-none
                  break-words
                  whitespace-normal
                  [&_*]:break-words
                  [&_*]:whitespace-normal
                  [&_img]:max-w-full [&_img]:h-auto
                  [&_table]:w-full [&_table]:block [&_table]:overflow-x-auto
                  [&_pre]:whitespace-pre-wrap [&_pre]:break-words
                  [&_code]:whitespace-pre-wrap [&_code]:break-words
                  pb-10 font-sf-regular
                "
              dangerouslySetInnerHTML={{ __html: fileContent }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.18 }}
            />
          ) : (
            <div className="italic text-gray-500">Không có nội dung.</div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BannerDetail;
